import React, { useEffect, useState } from "react";
import { Bar, Pie, Line } from "react-chartjs-2";
import {
  Skeleton,
  Card,
  Box,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  Grid,
  Paper,
  Container,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CodeIcon from "@mui/icons-material/Code";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import LayersIcon from "@mui/icons-material/Layers";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import WidgetSelection from "./WidgetSelection";
import Widget from "./Widget";
import { useAuth } from "../context/AuthProvider";
import { useNavigate } from "react-router-dom";
import Layout from "./Layout";
const ResponsiveGridLayout = WidthProvider(Responsive);
// const token = localStorage.getItem('access_token');

// Register Chart.js components globally
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend
);

// Widget data
const availableWidgets = [
  { name: "projectSummary", label: "Project Summary" },
  { name: "issuesSeverity", label: "Issues by Severity" },
  { name: "projectStatus", label: "Project Status Distribution" },
  // { name: "linesOfCode", label: "Lines of Code Over Time" },
];

// Default grid layout for widgets
const defaultLayouts = {
  lg: [
    { i: "projectSummary", x: 0, y: 0, w: 12, h: 2 },
    { i: "issuesSeverity", x: 0, y: 2, w: 6, h: 4 },
    { i: "projectStatus", x: 6, y: 2, w: 6, h: 4 },
    // { i: "linesOfCode", x: 0, y: 6, w: 6, h: 4 },
  ],
  md: [
    { i: "projectSummary", x: 0, y: 0, w: 10, h: 2 },
    { i: "issuesSeverity", x: 0, y: 2, w: 5, h: 4 },
    { i: "projectStatus", x: 5, y: 2, w: 5, h: 4 },
    // { i: "linesOfCode", x: 0, y: 6, w: 5, h: 4 },
  ],
  sm: [
    { i: "projectSummary", x: 0, y: 0, w: 12, h: 2 },
    { i: "issuesSeverity", x: 0, y: 4, w: 6, h: 4 },
    { i: "projectStatus", x: 0, y: 6, w: 6, h: 3 },
    // { i: "linesOfCode", x: 0, y: 8, w: 6, h: 3 },
  ],
  xs: [
    { i: "projectSummary", x: 0, y: 0, w: 8, h: 2 },
    { i: "issuesSeverity", x: 0, y: 4, w: 4, h: 4 },
    { i: "projectStatus", x: 0, y: 6, w: 4, h: 2 },
    // { i: "linesOfCode", x: 0, y: 8, w: 4, h: 2 },
  ],
  xxs: [
    { i: "projectSummary", x: 0, y: 0, w: 4, h: 2 },
    { i: "issuesSeverity", x: 0, y: 4, w: 2, h: 4 },
    { i: "projectStatus", x: 0, y: 6, w: 2, h: 2 },
    // { i: "linesOfCode", x: 0, y: 8, w: 2, h: 2 },
  ],
};

const Dashboard = () => {
  const { authToken, userRole } = useAuth();
  const token = authToken;
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedWidgets, setSelectedWidgets] = useState(() => {
    const storedSelections = localStorage.getItem("selectedWidgets");
    return storedSelections ? JSON.parse(storedSelections) : {};
  });
  const [layouts, setLayouts] = useState(() => {
    const storedLayouts = localStorage.getItem("layouts");
    return storedLayouts ? JSON.parse(storedLayouts) : { ...defaultLayouts };
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [chartData, setChartData] = useState({
    totalProjects: 0,
    completedProjects: 0,
    ongoingProjects: 0,
    pendingProjects: 0,
    codePushed: 0,
    codeLimit: 0,
    severityCounts: { info: 0, minor: 0, major: 0, critical: 0, blocker: 0 },
  });
  const [apiData, setApiData] = useState(null);
  const [severityData, setSeverityData] = useState(null);
  const [planName, setPlanName] = useState(null);
  const [selectedGranularity, setSelectedGranularity] = useState("day");
  const [selectedCumulation, setSelectedCumulation] = useState("sum");
  const [organization, setOrganization] = useState("");
  const [projectStatusData, setProjectStatusData] = useState({
    totalProjects: 0,
    completed: 0,
    failed: 0,
    inProgress: 0,
  });

  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        // setProjects(data);
        setOrganization(data.organization.name);
        setPlanName(data.organization.plan_name);
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
      });
  }, []);

  // Ensure layouts are validated on load
  useEffect(() => {
    const storedLayouts = localStorage.getItem("layouts");
    if (storedLayouts) {
      try {
        const parsedLayouts = JSON.parse(storedLayouts);

        // Ensure all widgets have a layout
        const updatedLayouts = { ...defaultLayouts }; // Start with defaults
        Object.keys(defaultLayouts).forEach((breakpoint) => {
          if (parsedLayouts[breakpoint]) {
            updatedLayouts[breakpoint] = [...parsedLayouts[breakpoint]];
            // Add default layout for missing widgets
            defaultLayouts[breakpoint].forEach((defaultWidget) => {
              if (
                !parsedLayouts[breakpoint].some((w) => w.i === defaultWidget.i)
              ) {
                updatedLayouts[breakpoint].push(defaultWidget);
              }
            });
          }
        });

        setLayouts(updatedLayouts);
      } catch {
        // Fall back to default layouts on error
        setLayouts({ ...defaultLayouts });
      }
    } else {
      // If no layouts in storage, set to defaults
      setLayouts({ ...defaultLayouts });
    }
  }, []);

  const handleWidgetSelectionChange = (newSelection) => {
    const updatedLayouts = { ...layouts };

    Object.keys(defaultLayouts).forEach((breakpoint) => {
      if (!updatedLayouts[breakpoint]) {
        updatedLayouts[breakpoint] = [];
      }

      // Add newly selected widgets
      Object.keys(newSelection).forEach((widgetKey) => {
        if (newSelection[widgetKey]) {
          // Add the widget if not already present
          if (!updatedLayouts[breakpoint].some((w) => w.i === widgetKey)) {
            const defaultWidgetLayout = defaultLayouts[breakpoint].find(
              (w) => w.i === widgetKey
            );
            if (defaultWidgetLayout) {
              const { x, y } = findNextAvailablePosition(
                updatedLayouts[breakpoint],
                defaultWidgetLayout.w,
                defaultWidgetLayout.h
              );
              updatedLayouts[breakpoint].push({
                ...defaultWidgetLayout,
                x,
                y,
              });
            }
          }
        } else {
          // Remove unselected widgets
          updatedLayouts[breakpoint] = updatedLayouts[breakpoint].filter(
            (w) => w.i !== widgetKey
          );
        }
      });

      // Reflow the layout after removal
      updatedLayouts[breakpoint] = reflowWidgets(updatedLayouts[breakpoint]);
    });

    setLayouts(updatedLayouts);
    setSelectedWidgets(newSelection);
    localStorage.setItem("selectedWidgets", JSON.stringify(newSelection));
    localStorage.setItem("layouts", JSON.stringify(updatedLayouts));
  };

  const findNextAvailablePosition = (layout, w, h) => {
    const GRID_COLUMNS = 12; // Adjust as per your grid system
    const occupiedSpaces = {};

    // Mark occupied spaces
    layout.forEach((widget) => {
      for (let x = widget.x; x < widget.x + widget.w; x++) {
        for (let y = widget.y; y < widget.y + widget.h; y++) {
          occupiedSpaces[`${x},${y}`] = true;
        }
      }
    });

    // Find next free position
    for (let y = 0; y < Infinity; y++) {
      for (let x = 0; x <= GRID_COLUMNS - w; x++) {
        let fits = true;
        // Check if space is available for the widget's size
        for (let dx = 0; dx < w; dx++) {
          for (let dy = 0; dy < h; dy++) {
            if (occupiedSpaces[`${x + dx},${y + dy}`]) {
              fits = false;
              break;
            }
          }
          if (!fits) break;
        }
        if (fits) return { x, y };
      }
    }
    return { x: 0, y: 0 }; // Fallback
  };

  const reflowWidgets = (layout) => {
    const sortedLayout = [...layout].sort((a, b) =>
      a.y === b.y ? a.x - b.x : a.y - b.y
    );
    const GRID_COLUMNS = 12;
    const reflowedLayout = [];

    sortedLayout.forEach((widget) => {
      const { x, y } = findNextAvailablePosition(
        reflowedLayout,
        widget.w,
        widget.h
      );
      reflowedLayout.push({ ...widget, x, y });
    });

    return reflowedLayout;
  };

  const handleLayoutChange = (currentLayout, allLayouts) => {
    const updatedLayouts = { ...allLayouts };
    setLayouts(updatedLayouts);
    localStorage.setItem("layouts", JSON.stringify(updatedLayouts));
  };

  const reorderWidgets = () => {
    setLayouts(defaultLayouts);
    localStorage.setItem("layouts", JSON.stringify(defaultLayouts));
  };

  // Dummy project statistics
  const dummyProjectStats = {
    totalProjects: 10,
    completedProjects: 7,
    ongoingProjects: 2,
    pendingProjects: 1,
    codePushed: 75000,
    codeLimit: 100000,
  };

  const processSeverityData = (data) => {
    if (!data || !data.issue_details_list) return null;

    const selectedvalue =
      selectedGranularity === "day"
        ? 7
        : selectedGranularity === "month"
        ? 6
        : 5;

    // Split the data to the requirement
    let processedData = data.issue_details_list.slice(0, selectedvalue);

    // Sort the data based on selectedGranularity
    processedData = processedData.sort((a, b) => {
      const formatDate = (dateString, selected) => {
        if (selected === "day") {
          const [day, month, year] = dateString.split("-").map(Number);
          return new Date(year, month - 1, day);
        } else if (selected === "month") {
          const [month, year] = dateString.split("-").map(Number);
          return new Date(year, month - 1);
        }
        return null;
      };

      const dateA = formatDate(a[selectedGranularity], selectedGranularity);
      const dateB = formatDate(b[selectedGranularity], selectedGranularity);

      return dateA - dateB;
    });

    // Map the sorted data to extract labels and severity details
    const labels = processedData.map((item) => item[selectedGranularity]);
    const infoSeverityData = processedData.map(
      (item) => item.severity_details.info || 0
    );
    const minorSeverityData = processedData.map(
      (item) => item.severity_details.minor || 0
    );
    const majorSeverityData = processedData.map(
      (item) => item.severity_details.major || 0
    );
    const criticalSeverityData = processedData.map(
      (item) => item.severity_details.critical || 0
    );
    const blockerSeverityData = processedData.map(
      (item) => item.severity_details.blocker || 0
    );

    // Return the formatted data for visualization
    return {
      labels,
      datasets: [
        {
          label: "Info Severity",
          data: infoSeverityData,
          backgroundColor: "#17A2B8",
        },
        {
          label: "Minor Severity",
          data: minorSeverityData,
          backgroundColor: "#6C757D",
        },
        {
          label: "Major Severity",
          data: majorSeverityData,
          backgroundColor: "#FFC107",
        },
        {
          label: "Critical Severity",
          data: criticalSeverityData,
          backgroundColor: "#DC3545",
        },
        {
          label: "Blocker Severity",
          data: blockerSeverityData,
          backgroundColor: "#343A40",
        },
      ],
    };
  };
  useEffect(() => {
    fetchAndProcessData();
  }, [selectedGranularity, selectedCumulation]);

  const fetchAndProcessData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/dashboard-issues-severity-widget?granularity=${selectedGranularity}&cumulation=${selectedCumulation}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }
      const data = await response.json();
      setApiData(data);

      // Process API data for the chart
      const processedSeverityData = processSeverityData(data);
      setSeverityData(processedSeverityData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectStatusData = async () => {
    setLoading(true);
    setError(null);
    try {
      // console.log("latest token " + token);
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/dashboard-project-status-widget`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch project status data");
      }

      const data = await response.json();
      setProjectStatusData({
        totalProjects: data.total_projects || 0,
        completed: data.completed || 0,
        failed: data.failed || 0,
        inProgress: data["in-progress"] || 0,
      });
    } catch (error) {
      console.error("Error fetching project status data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectStatusData();
  }, []);

  const pieChartData = {
    labels: ["Completed", "Failed", "In Progress"],
    datasets: [
      {
        data: [
          projectStatusData.completed,
          projectStatusData.failed,
          projectStatusData.inProgress,
        ],
        backgroundColor: ["#339e25", "#e73535", "#f7903c"], // Blue, Red, Yellow
        hoverBackgroundColor: ["#44c433", "#db4b4b", "#f0c23b"],
      },
    ],
  };
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/plan/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch plans");
        return res.json();
      })
      .then((data) => {
        setPlans(data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingPlans(false));
  }, []);

  // if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Layout>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "5px",
            p: 1,
          }}
        >
          <Box sx={{ ml: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
              Dashboard
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              {organization && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {organization}
                </Typography>
              )}

              {organization && planName && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  Plan:{planName}
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {userRole === "superadmin" && (
              <Button
                variant="contained"
                className="athrv-dash-primary-button"
                onClick={() => navigate("/admindashboard")}
                sx={{ whiteSpace: "nowrap" }}
              >
                ADMIN DASHBOARD
              </Button>
            )}
            <Button
              variant="contained"
              className="athrv-dash-primary-button"
              onClick={reorderWidgets}
              sx={{ whiteSpace: "nowrap" }}
            >
              Reorder Widgets
            </Button>
            <WidgetSelection
              availableWidgets={availableWidgets}
              onWidgetSelectionChange={handleWidgetSelectionChange}
            />
          </Box>
        </Box>

        {/* Responsive Grid Layout */}
        <ResponsiveGridLayout
          className="layout"
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
          rowHeight={100}
          isResizable={true}
          isDraggable={true}
          onLayoutChange={handleLayoutChange}
        >
          {selectedWidgets.projectSummary && (
            <div key="projectSummary">
              <Widget title="Project Summary">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  {loading ? (
                    [...Array(4)].map((_, index) => (
                      <Card
                        key={index}
                        elevation={2}
                        sx={{
                          p: 2,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          borderRadius: 2,
                          // backgroundColor: "#f0f0f0",
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height={100}
                          sx={{
                            borderRadius: 2.5,
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "#333"
                                : "rgba(0, 0, 0, 0.11)",
                          }}
                        />
                      </Card>
                    ))
                  ) : (
                    // Show actual data when loaded
                    <>
                      <Card
                        elevation={2}
                        sx={{
                          p: 2,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          borderRadius: 2,
                          backgroundColor: "#2a3955",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ color: "white", fontWeight: "bold", mb: 1 }}
                        >
                          Total Projects:
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: "bold", color: "white" }}
                        >
                          {projectStatusData.totalProjects}
                        </Typography>
                      </Card>

                      <Card
                        elevation={2}
                        sx={{
                          p: 2,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          borderRadius: 2,
                          backgroundColor: "#339e25",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ color: "white", fontWeight: "bold", mb: 1 }}
                        >
                          Completed Projects:
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: "bold", color: "white" }}
                        >
                          {projectStatusData.completed}
                        </Typography>
                      </Card>

                      <Card
                        elevation={2}
                        sx={{
                          p: 2,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          borderRadius: 2,
                          backgroundColor: "#e73535", // Background for Failed Projects
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ color: "white", fontWeight: "bold", mb: 1 }} // Text in yellow
                        >
                          Failed Projects:
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: "bold", color: "white" }}
                        >
                          {projectStatusData.failed}
                        </Typography>
                      </Card>

                      <Card
                        elevation={2}
                        sx={{
                          p: 2,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          borderRadius: 2,
                          backgroundColor: "#f7903c",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ color: "white", fontWeight: "bold", mb: 1 }} // Text in yellow
                        >
                          In Progress:
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: "bold", color: "white" }}
                        >
                          {projectStatusData.inProgress}
                        </Typography>
                      </Card>
                    </>
                  )}
                </Box>
              </Widget>
            </div>
          )}

          {selectedWidgets.issuesSeverity && (
            <div key="issuesSeverity">
              <Widget
                title="Issues by Severity"
                actions={
                  <>
                    <Select
                      value={selectedGranularity}
                      onChange={(e) => setSelectedGranularity(e.target.value)}
                      size="small"
                      sx={{ width: "100px" }}
                    >
                      <MenuItem value="day">Day</MenuItem>
                      <MenuItem value="month">Month</MenuItem>
                      <MenuItem value="year">Year</MenuItem>
                    </Select>
                    <Select
                      value={selectedCumulation}
                      onChange={(e) => setSelectedCumulation(e.target.value)}
                      size="small"
                      sx={{ width: "140px" }}
                    >
                      <MenuItem value="sum">Sum</MenuItem>
                      <MenuItem value="average">Average</MenuItem>
                    </Select>
                  </>
                }
              >
                <Box sx={{ flex: 1, overflow: "auto", maxHeight: "100%" }}>
                  {loading ? (
                    <CircularProgress />
                  ) : error ? (
                    <Alert severity="error">{error}</Alert>
                  ) : severityData ? (
                    <Bar
                      data={severityData}
                      options={{
                        // color: "auto",
                        responsive: true,
                        isResizable: true,
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            position: "top",
                            labels: {
                              color:
                                theme.palette.mode === "dark" ? "#fff" : "gray",
                            },
                          },
                        },
                        scales: {
                          x: {
                            ticks: {
                              color:
                                theme.palette.mode === "dark" ? "#fff" : "gray",
                            },
                          },
                          y: {
                            beginAtZero: true,
                            ticks: {
                              color:
                                theme.palette.mode === "dark" ? "#fff" : "gray",
                            },
                            title: {
                              display: true,
                              text: "Severity Count",
                              color:
                                theme.palette.mode === "dark" ? "#fff" : "gray",
                            },
                          },
                        },
                      }}
                    />
                  ) : (
                    <Typography>No data available</Typography>
                  )}
                </Box>
              </Widget>
            </div>
          )}
          {selectedWidgets.projectStatus && (
            <div key="projectStatus">
              <Widget title="Project Status Distribution">
                <Pie
                  data={pieChartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        labels: {
                          color:
                            theme.palette.mode === "dark" ? "#fff" : "gray",
                        },
                      },
                      tooltip: {
                        bodyColor:
                          theme.palette.mode === "dark" ? "#fff" : "gray",
                        titleColor:
                          theme.palette.mode === "dark" ? "#fff" : "gray",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#424242" : "#fff",
                      },
                    },
                  }}
                />
              </Widget>
            </div>
          )}

          {/* {selectedWidgets.linesOfCode && (
          <div key="linesOfCode">
            <Widget title="Lines of Code Over Time">
              <Line data={linesOfCodeData} options={{ responsive: true }} />
            </Widget>
          </div>
        )} */}
        </ResponsiveGridLayout>
      </Box>
    </Layout>
  );
};

export default Dashboard;
